import { Component, Inject, OnInit } from "@angular/core";
import { ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { NOTIFICATIONCONST, Payload } from "../../../../../components/nav-bar/Models/notification.model";
import { MeetingOrganizerResponse } from "../../../models/meeting.model";
import { format } from "date-fns";
import { CreateMeetingService } from "../../../services/create-meeting.service";
import { SharedService } from "../../../../../services/shared.service";
import { ToastrService } from "ngx-toastr";
import { HandleErrorService } from "../../../../../services/handle-error-service";
import { SharedModule } from "../../../../shared/shared.module";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";

@Component({
    selector: "app-meeting-organizer-response",
    templateUrl: "./meeting-organizer-response.component.html",
    styleUrls: ["./meeting-organizer-response.component.scss"],
    imports: [
        SharedModule,
        MatInputModule,
        MatDialogModule,
        MatButtonModule,
        MatDatepickerModule,
        MatButtonToggleModule,
        NgxMaterialTimepickerModule,
        ReactiveFormsModule,
    ],
    standalone: true,
})
export class MeetingOrganizerResponseComponent implements OnInit {
    meetingStatusQuestion: string = "";
    dateLabel: string = "";
    timeLabel: string = "";
    meetingStatusControl: UntypedFormControl = new UntypedFormControl();
    meetingDateControl: UntypedFormControl = new UntypedFormControl();
    meetingTimeControl: UntypedFormControl = new UntypedFormControl();
    meetingPayload: Payload;
    isMeetingStartConfirmation: boolean;
    maxDate: string | undefined = undefined;

    constructor(
        @Inject(MAT_DIALOG_DATA) private dialogData: any,
        private createMeetingService: CreateMeetingService,
        private sharedService: SharedService,
        private toastr: ToastrService,
        private handleErrorService: HandleErrorService,
        private dialogRef: MatDialogRef<MeetingOrganizerResponseComponent>,
    ) {
        this.meetingPayload = this.dialogData.meetingPayload;
        if (this.meetingPayload.actualType && this.meetingPayload.actualType.length > 0) {
            if (this.meetingPayload.actualType === NOTIFICATIONCONST.NotificationType.START_MEETING_CONFIRMATION) {
                this.meetingStatusQuestion = "Is Meeting Started ?";
                this.isMeetingStartConfirmation = true;
                this.maxDate = undefined;
            } else if (this.meetingPayload.actualType === NOTIFICATIONCONST.NotificationType.END_MEETING_CONFIRMATION) {
                this.meetingStatusQuestion = "Is Meeting Ended ?";
                this.isMeetingStartConfirmation = false;
                this.maxDate = new Date().toISOString();
            }
        }
    }

    ngOnInit(): void {
        this.meetingStatusControl.valueChanges.subscribe((value) => {
            if (value === 1) {
                this.handleYesConfirmation();
            } else {
                this.handleNoConfirmation();
            }
        });
    }

    handleYesConfirmation() {
        if (this.isMeetingStartConfirmation) {
            this.dateLabel = "Meeting Start Date";
            this.timeLabel = "Meeting Start Time";
            if (this.meetingPayload.meetingDate && this.meetingPayload.meetingDate.length > 0) {
                let meetingDate = new Date(this.meetingPayload.meetingDate);

                // Maintaining format "2023-04-27T12:40:00",
                let meetingTime = String(meetingDate.getHours()).padStart(2, "0") + ":" + String(meetingDate.getMinutes()).padStart(2,
                    "0");
                this.meetingTimeControl.setValue(meetingTime);
            }
        } else {
            this.dateLabel = "Meeting End Date";
            this.timeLabel = "Meeting End Time";
        }
    }

    handleNoConfirmation() {
        if (this.meetingPayload.meetingId && this.meetingPayload.meetingId.length > 0) {
            let meetingOrganizerResponse = new MeetingOrganizerResponse();
            meetingOrganizerResponse.meetingId = Number(this.meetingPayload.meetingId);
            if (this.isMeetingStartConfirmation) {
                meetingOrganizerResponse.hasMeetingStarted = false;
                meetingOrganizerResponse.actualStartDateTime = null;
            } else {
                meetingOrganizerResponse.hasMeetingEnded = false;
                meetingOrganizerResponse.actualEndDateTime = null;
            }
            this.updateMeetingResponseToServer(meetingOrganizerResponse);
        }
    }

    updateMeetingResponseWithYes() {
        if (this.meetingPayload.meetingId && this.meetingPayload.meetingId.length > 0) {
            let meetingOrganizerResponse = new MeetingOrganizerResponse();
            meetingOrganizerResponse.meetingId = Number(this.meetingPayload.meetingId);
            const dateStr = format(new Date(this.meetingDateControl.value), "yyyy-MM-dd");
            const timeStr = this.formatTimePadded(this.meetingTimeControl.value);
            if (this.isMeetingStartConfirmation) {
                meetingOrganizerResponse.hasMeetingStarted = true;
                meetingOrganizerResponse.actualStartDateTime = `${dateStr}T${timeStr}:00`;
            } else {
                meetingOrganizerResponse.hasMeetingEnded = true;
                meetingOrganizerResponse.actualEndDateTime = `${dateStr}T${timeStr}:00`;
            }
            this.updateMeetingResponseToServer(meetingOrganizerResponse);
        }
    }

    private formatTimePadded(time: string): string {
        const [hours, minutes] = time.split(":");
        return String(hours).padStart(2, "0") + ":" + String(minutes).padStart(2, "0");
    }

    private updateMeetingResponseToServer(meetingOrganizerResponse: MeetingOrganizerResponse) {
        this.sharedService.showSpinner();
        const userAccountIds = this.sharedService.getAllAccountIdsOfLoggedInUser() as string;
        this.createMeetingService.updateMeetingOrganizerResponse(meetingOrganizerResponse, userAccountIds).subscribe(
            (response) => {
                this.toastr.success("Meeting Status Updated Successfully");
                this.sharedService.hideSpinner();
                this.dialogRef.close();
            },
            (error) => {
                this.handleErrorService.handleError(error);
                this.sharedService.hideSpinner();
                this.dialogRef.close();
            },
        );
    }
}
